import { type ListMemoryThreadsResponse } from '@mastra/client-js';
import { useQuery } from '@tanstack/react-query';

import { captureException } from '../../logger/Logger';
import { useMastraClient } from '../providers/MastraClient';

const MEMORY_THREADS_FETCH_PAGE_SIZE = 500;

export const useListMastraThreads = ({
    agentId,
    enabled = true,
}: {
    agentId: string;
    enabled?: boolean;
}) => {
    const mastraClient = useMastraClient();

    return useQuery({
        queryKey: ['mastraMemoryThreads', agentId],
        queryFn: async () => {
            try {
                const threads: ListMemoryThreadsResponse['threads'] = [];
                const seenIds = new Set<string>();
                let page = 0;
                let hasMore = true;

                while (hasMore) {
                    // eslint-disable-next-line no-await-in-loop -- sequential pagination so we don't load every thread page at once
                    const response = await mastraClient.listMemoryThreads({
                        agentId,
                        metadata: { agentId },
                        orderBy: { field: 'updatedAt', direction: 'DESC' },
                        page,
                        perPage: MEMORY_THREADS_FETCH_PAGE_SIZE,
                    });

                    const pageThreads = response.threads ?? [];
                    let added = 0;
                    for (const thread of pageThreads) {
                        if (!seenIds.has(thread.id)) {
                            seenIds.add(thread.id);
                            threads.push(thread);
                            added += 1;
                        }
                    }

                    hasMore =
                        added > 0 &&
                        pageThreads.length === MEMORY_THREADS_FETCH_PAGE_SIZE &&
                        response.hasMore;
                    page += 1;
                }

                return threads;
            } catch (err) {
                captureException(err);
                throw err;
            }
        },
        enabled,
        refetchOnWindowFocus: false,
    });
};
